import { ReactNode, useState } from "react"
import TreeNode from "./TreeNode"
import { TreeContext } from "./TreeProvider"
import CreateTreeEntryButton from "./TreeCreateEntryButton"

const findAncestors = (entry: DataRowProps, entries: DataRowProps[]): number[] => {
  if (entry.parent_id === -1) {
    return [entry.id]
  }
  const parentIds = entries
    .filter((e) => e.id === entry.parent_id)
    .flatMap((e) => findAncestors(e, entries))
  parentIds.push(entry.id)
  return [...new Set(parentIds)]
}

export interface DataRowProps {
  id: number
  parent_id: number
  label: string
  expanded: boolean
  level: number
  can_create: boolean
  can_delete: boolean
  can_copy: boolean
  can_edit: boolean
  can_move: boolean
  can_select: boolean
  [fieldName: string]: number | string | boolean
}

export interface TreeProps {
  /**
   * The ID used to identify this component in Dash callbacks.
   */
  id: string
  /**
   * Array of dicts containing the tree entries information.
   */
  data: DataRowProps[]
  /**
   * Index of the entry in the internal data array, useful for patching the data with dash Patch
   */
  entry_index?: number
  /**
   * ID of the last entry receiving an action
   */
  entry_id?: number
  /**
   * Increments when a delete button is clicked
   */
  n_delete?: number
  /**
   * Increments when a copy button is clicked
   */
  n_copy?: number
  /**
   * Increments when an edit button is clicked
   */
  n_edit?: number
  /**
   * Increments when a move button is clicked
   */
  n_move?: number
  /**
   * Increments when a 'create' button is clicked
   */
  n_create?: number
  /**
   * Increments when a 'custom' field is modified
   */
  n_custom?: number
  /**
   * ID of the entry that is selected OR list of IDs if enable_multi_select is true
   */
  selected_id?: number|number[]
  /**
   * Name of the field being modified
   */
  modified_field?: string
  /**
   * Value of the field being modified
   */
  modified_value?: string
  /**
   * Dictionary specifying what extra fields to show as additional cells in the tree. The keys of the dictionary are
   * the names of the extra fields passed in the 'data' prop.
   */
  columns?: {
    [fieldName: string]: {
      /**
       * Type of the column, can be:
       * - 'input-number': It will allow setting a number
       * - 'input-number-highlight': It will allow setting a number, it will highlight this cell
       * - 'read': It will be a read only text field
       * - 'score': It will be a spend matters icon representing a relative score
       */
      type: "input-number" | "input-number-highlight" | "read" | "score"
      /**
       * Specify the width in pixels that the cells will occupy
       */
      width: number
      /**
       * Specify the class for the cell
       */
      className: string
    }
  }
  /**
   * If can_move is true, defines the direction of the 'move' icon. It can be:
   * - left
   * - right (default)
   * - up
   * - down
   */
  move_direction?: string
  /**
   * If true, it allows to add entries in the root of the tree (default: false)
   */
  enable_create?: boolean
  /**
   * If true, it allows to edit all entries, ignoring the "can_edit" parameter from each row (default: false)
   */
  enable_edit?: boolean
  /**
   * If true, it allows to copy all entries, ignoring the "can_copy" parameter from each row (default: false)
   */
  enable_copy?: boolean
  /**
   * If true, it allows to delete all entries, ignoring the "can_delete" parameter from each row (default: false)
   */
  enable_delete?: boolean
  /**
   * If true, allows to select multiple elements/nodes (in this case selected_id will be a list)
   */
  enable_multi_select?: boolean
  /**
   * Defines CSS styles which will override styles previously set.
   */
  style?: { [cssProp: string]: number | string }
  /**
   * Defines CSS styles for the header which will override styles previously set.
   */
  header_style?: { [cssProp: string]: number | string }
  /**
   * Often used with CSS to style elements with common properties.
   */
  className?: string
  /**
   * Often used with CSS to style elements with common properties.
   */
  header_className?: string
  /**
   * Dict where the keys are the levels and the values are strings indicating the className to apply to that level
   */
  level_className?: { [level: number]: string }
  /**
   * If specified, it will show only elements from that level
   * Parents are still required to be present in the data array if InclexPanel is used
   */
  single_level?: number
  /**
   * Text to be displayed in a tooltip when hovering over the element.
   */
  title?: string
  /**
   * The children of this component.
   */
  children?: ReactNode
  /**
   * Dash-assigned callback that should be called to report property changes
   * to Dash, to make them available for callbacks.
   */
  setProps: (props: Partial<TreeProps>) => void
}

/**
 * Tree is a component to display, search and perform CRUD operations on hierarchical data
 */
const Tree = ({
  n_delete = 0,
  n_copy = 0,
  n_edit = 0,
  n_move = 0,
  n_create = 0,
  n_custom = 0,
  move_direction = "right",
  enable_create = false,
  enable_edit = undefined,
  enable_copy = undefined,
  enable_delete = undefined,
  enable_multi_select = false,
  header_style = {},
  header_className = "",
  level_className = {},
  ...props
}: TreeProps) => {
  const {
    id,
    style,
    className,
    title,
    data,
    columns,
    selected_id,
    single_level,
    children,
    setProps,
  } = props

  if (move_direction == null) move_direction = "right"
  if (enable_create == null) enable_create = false
  if (enable_edit == null) enable_edit = undefined
  if (enable_copy == null) enable_copy = undefined
  if (enable_delete == null) enable_delete = undefined
  if (header_style == null) header_style = {}
  if (header_className == null) header_className = ""
  if (level_className == null) level_className = {}

  const [search, setSearch] = useState("")
  const [sortById, setSortById] = useState(true)

  const sortedData = sortById
    ? data.sort((a, b) => a.id - b.id)
    : data.sort((a, b) => a.label.localeCompare(b.label))

  let matchedIds: number[] = []
  let expandedIds: number[] = []
  if (search.length > 2) {
    const matchedEntries = sortedData.filter(
      (entry) =>
        entry.label.toLowerCase().includes(search.toLowerCase()) &&
        (single_level == undefined || entry.level == single_level)
    )
    matchedIds = matchedEntries.map((entry) => entry.id)
    if (single_level == undefined) {
      const matchedParentIds = [...new Set(matchedEntries.map((entry) => entry.parent_id))]
      const matchedEntriesParents = sortedData.filter((entry) =>
        matchedParentIds.includes(entry.id)
      )
      expandedIds = matchedEntriesParents.flatMap((entry) => findAncestors(entry, sortedData))
    }
  }
  // Expand selected entry
  const selectedEntry = sortedData.find((entry) => entry.id === selected_id)
  if (selectedEntry != undefined) expandedIds.push(...findAncestors(selectedEntry, sortedData))

  const fields = sortedData[0]
  const descendants =
    single_level == undefined
      ? sortedData.filter((entry) => entry.parent_id === -1)
      : sortedData.filter((entry) => entry.level === single_level)
  const sortingIcon = sortById ? "Id" : "Label"

  return (
    <div className={className}>
      <div style={header_style}>
        <div className="a-middle">
          <div className="tree-search-bar">
            <i className="bi bi-search purple border-end px-3" />
            <input
              onChange={(e) => setSearch(e.target.value.length > 2 ? e.target.value : "")}
              className="p-2 search-bar-input-tree"
              placeholder="Type to search..."
            />
          </div>
          <div
            onClick={() => setSortById((sortById) => !sortById)}
            className="ms-2 sm-button sm-button-purple text-nowrap"
            style={{ minWidth: "70px" }}
          >
            <i className="bi bi-arrow-down-up me-2" />
            {sortingIcon}
          </div>
        </div>
        {columns && (
          <div
            className={`a-middle-between pt-2 pb-1 fs-5 ${header_className}`}
          >
            <div>{children}</div>
            <div className="a-middle">
              {fields &&
                Object.entries(columns).map(
                  ([col, p]) =>
                    col in fields && (
                      <div className={p.className} style={{ width: p.width }}>
                        {col}
                      </div>
                    )
                )}
            </div>
          </div>
        )}
      </div>
      <div id={id} style={style} title={title}>
        <TreeContext.Provider
          value={{
            nDelete: n_delete,
            nCopy: n_copy,
            nEdit: n_edit,
            nMove: n_move,
            nCreate: n_create,
            nCustom: n_custom,
            columns: columns,
            moveDirection: move_direction,
            levelClassName: level_className,
            enableEdit: enable_edit,
            enableCopy: enable_copy,
            enableDelete: enable_delete,
            enableMultiSelect: enable_multi_select,
            selectedId: selected_id,
            matchedIds: matchedIds,
            expandedIds: expandedIds
          }}
        >
          {enable_create && (
            <CreateTreeEntryButton
              index={single_level == undefined ? -1 : single_level - 1}
              id={single_level == undefined ? -1 : single_level - 1}
              setProps={setProps}
            />
          )}
          {descendants.map((child) => (
            <TreeNode
              id={child.id}
              key={child.id}
              treeData={sortedData}
              setProps={setProps}
              selectedId={selected_id}
              singleLevel={single_level}
            />
          ))}
        </TreeContext.Provider>
      </div>
    </div>
  )
}

export default Tree
